import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Position, PositionDraft, PositionsUiState } from '../types/project';
import type { ReportDelivery } from '../entities/project/model/types';
import { UPLOAD_LIMITS } from '../data/projectCard';
import { REPORT_STATUS } from '../entities/project/model/reportStatus';
import { parsePositionsFile } from '../entities/project/lib/parsePositionsFile';
import { editsToRestartBody, positionToSearchProduct } from '../entities/project/model/mappers';
import type { ReportDto } from '../entities/project/model/dto';
import {
    useAddProductMutation,
    useDeleteProductMutation,
    useGetProductsQuery,
    useGetProjectReportQuery,
    useSetDeliveryAddressMutation,
    useStartProductsSearchMutation,
    useGetReportDeliveryQuery,
    useRestartProductMutation,
    useCheckScreenshotsStatusQuery,
} from '../entities/project/api/projectApi';
import { useAppSelector } from '../app/hooks';
import { selectUser } from '../entities/session/model/slice';
import { loadDrafts, saveDrafts } from '../entities/project/model/draftStorage';

const SUCCESS_SCREEN_MS = 1500;

const REPORT_POLL_MS = 15000;

const PRODUCTS_POLL_MS = 4000;
const DELIVERY_POLL_MS = 2500;
const SCREENSHOTS_POLL_MS = 15000;

const AWAIT_SEARCH_TIMEOUT_MS = 20000;

const LOCAL_ID_PREFIX = 'local:';

const isLocalId = (id: string): boolean => id.startsWith(LOCAL_ID_PREFIX);

const RESTART_FIELDS = new Set<keyof Position>([
    'name',
    'spec',
    'region',
    'qty',
    'unit',
    'priceFrom',
    'priceTo',
    'requestPrice',
    'analogs',
    'logistics',
]);

const draftToLocalPosition = (draft: PositionDraft, index: number): Position => ({
    ...draft,
    id: `${LOCAL_ID_PREFIX}${index}:${Date.now()}`,
    deliveryWithoutPackaging: 0,
    packagingCost: 0,
    analogsDelivery: 'not_searched',
});

export interface SearchProgress {
    done: number;
    total: number;
    percent: number;
}

export interface UseProjectPositions {
    positions: Position[];
    uiState: PositionsUiState;
    page: number;
    pageCount: number;
    total: number;
    deliveryAvgTotal: number;
    isPageLoading: boolean;
    setPage: (value: number) => void;
    lastFileName: string;
    deliveryAddress: string;
    logisticsCount: number;
    reportId: string;
    reportStatus: string;
    screenshots?: ReportDto['screenshots'];
    isScreenshotting: boolean;
    isDeliveryProcessing: boolean;
    delivery?: ReportDelivery;
    progress: SearchProgress;
    isLoading: boolean;
    isSearching: boolean;
    awaitingSearch: boolean;
    isReportFinished: boolean;
    pendingCount: number;
    isStartingSearch: boolean;
    setSearchRunning: (value: boolean) => void;
    addPositions: (drafts: PositionDraft[]) => Promise<void>;
    removePosition: (id: string) => Promise<void>;
    updatePosition: (id: string, patch: Partial<Position>) => Promise<void>;
    toggleFlag: (id: string, key: 'analogs' | 'logistics') => Promise<void>;
    setFlagAll: (key: 'analogs' | 'logistics', value: boolean) => number;
    dirtyIds: Set<string>;
    /** Позиции, изменённые общими свитчами (кнопка в строке для них скрыта). */
    bulkDirtyIds: Set<string>;
    restartPosition: (id: string, patch?: Partial<Position>) => Promise<boolean>;
    discardEdits: (id: string) => void;
    clearAll: () => Promise<void>;
    clearDrafts: () => Promise<void>;
    setDeliveryAddress: (value: string) => void;
    commitDeliveryAddress: () => Promise<boolean>;
    handleFile: (file: File | null) => Promise<string | null>;
    runSearch: (projectRegion?: string) => Promise<{ started: number; duplicates: number } | string>;
    branchesLeft: number;
    restoredCount: number;
}

export function useProjectPositions(projectId: string): UseProjectPositions {
    const user = useAppSelector(selectUser);
    const userId = user?.id ?? '';

    const [forcePolling, setForcePolling] = useState(false);
    const [restartProduct] = useRestartProductMutation();

    const [productsFailed, setProductsFailed] = useState(false);

    const {
        data: report,
        isLoading: isReportLoading,
        isUninitialized: isReportUninitialized,
    } = useGetProjectReportQuery(projectId, {
        skip: !projectId,
        pollingInterval: REPORT_POLL_MS,
    });

    const reportId = report?.id ?? '';
    const reportStatus = report?.status ?? '';

    const isReportBusy =
        reportStatus === REPORT_STATUS.PROCESSING || reportStatus === REPORT_STATUS.OPTIMIZED;

    const isScreenshotting = reportStatus === 'screenshotting';

    useCheckScreenshotsStatusQuery(reportId, {
        skip: !reportId || !isScreenshotting,
        pollingInterval: SCREENSHOTS_POLL_MS,
    });

    const [page, setPage] = useState(1);

    const [deliveryPendingRows, setDeliveryPendingRows] = useState(false);

    const [deliveryPolling, setDeliveryPolling] = useState(true);

    const { data: delivery, refetch: refetchDelivery } = useGetReportDeliveryQuery(
        { projectId, reportId },
        {
            skip: !reportId,
            pollingInterval: deliveryPolling || isReportBusy ? DELIVERY_POLL_MS : 0,
        },
    );

    const {
        data: productsPage,
        isLoading: isProductsLoading,
        isUninitialized: isProductsUninitialized,
        isFetching: isProductsFetching,
        isError: isProductsError,
        refetch: refetchProducts,
    } = useGetProductsQuery(
        { reportId, page },
        {
            skip: !reportId,
            pollingInterval:
                !productsFailed &&
                    (isReportBusy || forcePolling || deliveryPendingRows || delivery?.isProcessing)
                    ? PRODUCTS_POLL_MS
                    : 0,
        },
    );

    const isReportPending = !projectId || isReportUninitialized || isReportLoading;

    const isProductsPending = Boolean(reportId) && (isProductsUninitialized || isProductsLoading);

    const wasDeliveryProcessing = useRef(false);

    useEffect(() => {
        if (!delivery) return;

        if (wasDeliveryProcessing.current && !delivery.isProcessing) {
            void refetchProducts();
        }

        wasDeliveryProcessing.current = delivery.isProcessing;
        setDeliveryPolling(delivery.isProcessing);
    }, [delivery, refetchProducts]);

    const wasReportBusy = useRef(false);

    useEffect(() => {
        if (wasReportBusy.current && !isReportBusy && reportId) {
            void refetchDelivery();
            void refetchProducts();
        }
        wasReportBusy.current = isReportBusy;
    }, [isReportBusy, reportId, refetchDelivery, refetchProducts]);

    useEffect(() => {
        if (isProductsError) {
            setProductsFailed(true);
            setForcePolling(false);
        }
    }, [isProductsError]);

    const serverPositions = useMemo(() => productsPage?.items ?? [], [productsPage]);

    const shownPage = useRef(page);
    if (!isProductsFetching) shownPage.current = page;

    const isPageLoading = isProductsFetching && shownPage.current !== page;

    const total = productsPage?.total ?? 0;
    const pageCount = Math.max(1, productsPage?.pages ?? 1);
    const deliveryAvgTotal = productsPage?.deliveryAvgTotal ?? 0;
    const isSearching = productsPage?.isSearching ?? false;

    useEffect(() => {
        setDeliveryPendingRows(Boolean(productsPage?.isDeliveryPending));
    }, [productsPage]);

    useEffect(() => {
        if (
            forcePolling &&
            productsPage &&
            !productsPage.isSearching &&
            !productsPage.isDeliveryPending
        ) {
            setForcePolling(false);
        }
    }, [forcePolling, productsPage]);

    const progress = useMemo<SearchProgress>(() => {
        const total = productsPage?.total ?? 0;
        const done = productsPage?.finished ?? 0;
        return {
            done,
            total,
            percent: total ? Math.round((done / total) * 100) : 0,
        };
    }, [productsPage]);

    const [addProduct] = useAddProductMutation();
    const [deleteProduct] = useDeleteProductMutation();
    const [saveAddress] = useSetDeliveryAddressMutation();
    const [startProductsSearch, { isLoading: isStartingSearch }] =
        useStartProductsSearchMutation();

    const [lastFileName, setLastFileName] = useState('');
    const [isParsing, setIsParsing] = useState(false);
    const [justUploaded, setJustUploaded] = useState(false);
    const [pendingPositions, setPendingPositions] = useState<Position[]>([]);
    const [edits, setEdits] = useState<Record<string, Partial<Position>>>({});
    const [bulkIds, setBulkIds] = useState<Set<string>>(new Set());
    const [addressDraft, setAddressDraft] = useState<string | null>(null);
    const [totalFloor, setTotalFloor] = useState(0);

    const [awaitingSearch, setAwaitingSearch] = useState(false);
    const floorArmed = useRef(false);

    useEffect(() => {
        if (!floorArmed.current) return;
        floorArmed.current = false;
        setTotalFloor(0);
    }, [productsPage]);

    useEffect(() => {
        if (!awaitingSearch) return;
        if (serverPositions.length > 0) setAwaitingSearch(false);
    }, [awaitingSearch, serverPositions]);

    useEffect(() => {
        if (!awaitingSearch) return undefined;

        const timer = setTimeout(() => setAwaitingSearch(false), AWAIT_SEARCH_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [awaitingSearch]);

    const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (successTimer.current) clearTimeout(successTimer.current);
        },
        [],
    );

    /* ─── черновики позиций в localStorage ─── */

    const [restoredCount, setRestoredCount] = useState(0);

    const hydrated = useRef(false);

    useEffect(() => {
        hydrated.current = false;
        setRestoredCount(0);
        setAwaitingSearch(false);
        setBulkIds(new Set());
    }, [projectId]);

    useEffect(() => {
        if (hydrated.current || !projectId || !userId) return;
        if (isReportPending) return;
        if (isProductsPending) return;

        hydrated.current = true;

        const stored = loadDrafts(userId, projectId);
        if (!stored?.positions.length) return;

        setPendingPositions(stored.positions);
        if (stored.fileName) setLastFileName(stored.fileName);
        setRestoredCount(stored.positions.length);
    }, [projectId, userId, isReportPending, isProductsPending]);

    useEffect(() => {
        if (!hydrated.current || !projectId || !userId) return;
        saveDrafts(userId, projectId, pendingPositions, { fileName: lastFileName });
    }, [pendingPositions, lastFileName, projectId, userId]);

    const deliveryAddress = addressDraft ?? report?.delivery_address ?? '';

    const positionsWithFlags = useMemo(
        () =>
            serverPositions.map((position) => ({
                ...position,
                ...edits[position.id],
            })),
        [serverPositions, edits],
    );

    const positions = useMemo(
        () => [...positionsWithFlags, ...pendingPositions],
        [positionsWithFlags, pendingPositions],
    );

    const positionsRef = useRef<Position[]>(positions);
    positionsRef.current = positions;

    const serverRef = useRef<Position[]>(serverPositions);
    serverRef.current = serverPositions;

    const originalsRef = useRef<Record<string, Position>>({});

    const rememberOriginal = useCallback((id: string) => {
        if (originalsRef.current[id]) return;
        const base = serverRef.current.find((item) => item.id === id);
        if (base) originalsRef.current[id] = base;
    }, []);

    const dirtyIds = useMemo(() => new Set(Object.keys(edits)), [edits]);

    /* ─── пометка «изменено общим свитчем» ─── */

    const unmarkBulk = useCallback((id: string) => {
        setBulkIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, []);

    // только те, что всё ещё изменены — после перезапуска/отмены уходят сами
    const bulkDirtyIds = useMemo(
        () => new Set(Array.from(bulkIds).filter((id) => edits[id])),
        [bulkIds, edits],
    );

    const uiState: PositionsUiState = useMemo(() => {
        if (isParsing) return 'loading';
        if (justUploaded) return 'success';
        if (positions.length > 0) return 'list';

        if (isReportPending || isProductsPending || awaitingSearch) return 'fetching';

        return 'empty';
    }, [
        isParsing,
        justUploaded,
        positions.length,
        isReportPending,
        isProductsPending,
        awaitingSearch,
    ]);

    const addPositions = useCallback(async (drafts: PositionDraft[]) => {
        if (!drafts.length) return;

        setPendingPositions((prev) => [
            ...prev,
            ...drafts.map((draft, index) => draftToLocalPosition(draft, prev.length + index)),
        ]);
    }, []);

    const updatePosition = useCallback(
        async (id: string, patch: Partial<Position>) => {
            if (isLocalId(id)) {
                setPendingPositions((prev) =>
                    prev.map((position) => (position.id === id ? { ...position, ...patch } : position)),
                );
                return;
            }

            if (!reportId) return;

            rememberOriginal(id);
            unmarkBulk(id);
            setEdits((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
        },
        [reportId, rememberOriginal, unmarkBulk],
    );

    const restartPosition = useCallback(
        async (id: string, patch?: Partial<Position>): Promise<boolean> => {
            if (!reportId || isLocalId(id)) return false;

            const base =
                originalsRef.current[id] ?? serverRef.current.find((item) => item.id === id);
            const merged: Partial<Position> = { ...edits[id], ...patch };

            const changed: Partial<Position> = {};
            (Object.keys(merged) as (keyof Position)[]).forEach((key) => {
                if (!RESTART_FIELDS.has(key)) return;
                if (base && merged[key] === base[key]) return;
                Object.assign(changed, { [key]: merged[key] });
            });

            try {
                await restartProduct({
                    reportId,
                    productId: id,
                    body: editsToRestartBody(changed),
                }).unwrap();
            } catch {
                return false; /* сообщение об ошибке уже показано мутацией */
            }

            setEdits((prev) => {
                if (!prev[id]) return prev;
                const next = { ...prev };
                delete next[id];
                return next;
            });
            delete originalsRef.current[id];
            setForcePolling(true);
            return true;
        },
        [reportId, restartProduct, edits],
    );

    const discardEdits = useCallback((id: string) => {
        delete originalsRef.current[id];

        setEdits((prev) => {
            if (!prev[id]) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
    }, []);

    const removePosition = useCallback(
        async (id: string) => {
            if (isLocalId(id)) {
                setPendingPositions((prev) => prev.filter((position) => position.id !== id));
                return;
            }
            if (!reportId) return;
            await deleteProduct({ reportId, productId: id }).unwrap();
        },
        [deleteProduct, reportId],
    );

    const toggleFlag = useCallback(
        async (id: string, key: 'analogs' | 'logistics') => {
            if (isLocalId(id)) {
                setPendingPositions((prev) =>
                    prev.map((position) =>
                        position.id === id ? { ...position, [key]: !position[key] } : position,
                    ),
                );
                return;
            }

            const position = positionsRef.current.find((item) => item.id === id);
            if (!position) return;

            rememberOriginal(id);
            unmarkBulk(id);
            setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [key]: !position[key] } }));
        },
        [rememberOriginal, unmarkBulk],
    );

    /** Ставит флаг всем позициям (черновики + текущая страница). Возвращает число изменённых. */
    const setFlagAll = useCallback(
        (key: 'analogs' | 'logistics', value: boolean): number => {
            const targets = positionsRef.current.filter((item) => Boolean(item[key]) !== value);
            if (!targets.length) return 0;

            const localIds = new Set(
                targets.filter((item) => isLocalId(item.id)).map((item) => item.id),
            );
            if (localIds.size) {
                setPendingPositions((prev) =>
                    prev.map((item) => (localIds.has(item.id) ? { ...item, [key]: value } : item)),
                );
            }

            const server = targets.filter((item) => !isLocalId(item.id));
            if (server.length) {
                const next = { ...edits };
                const marked: string[] = [];

                server.forEach((item) => {
                    rememberOriginal(item.id);
                    const wasDirty = Boolean(edits[item.id]);
                    const original = originalsRef.current[item.id];
                    const edit: Partial<Position> = { ...next[item.id], [key]: value };

                    // вернули исходное значение — это уже не правка
                    if (original && Boolean(original[key]) === value) delete edit[key];

                    if (Object.keys(edit).length) {
                        next[item.id] = edit;
                        // точечно изменённые раньше сохраняют кнопку в строке
                        if (!wasDirty) marked.push(item.id);
                    } else {
                        delete next[item.id];
                        delete originalsRef.current[item.id];
                    }
                });

                setEdits(next);
                if (marked.length) setBulkIds((prev) => new Set(Array.from(prev).concat(marked)));
            }

            return targets.length;
        },
        [edits, rememberOriginal],
    );

    const clearAll = useCallback(async () => {
        setPendingPositions([]);
        setLastFileName('');

        if (!reportId) return;
        await Promise.all(
            serverPositions.map((position) =>
                deleteProduct({ reportId, productId: position.id }).unwrap(),
            ),
        );
    }, [deleteProduct, serverPositions, reportId]);

    const clearDrafts = useCallback(async () => {
        setPendingPositions([]);
        setLastFileName('');
        setRestoredCount(0);
    }, []);

    const commitDeliveryAddress = useCallback(async (): Promise<boolean> => {
        if (!reportId || addressDraft === null) return false;

        const next = addressDraft.trim();
        const saved = (report?.delivery_address ?? '').trim();

        if (next === saved) {
            setAddressDraft(null);
            return false;
        }

        await saveAddress({ reportId, address: next }).unwrap();
        setAddressDraft(null);
        return true;
    }, [addressDraft, reportId, report?.delivery_address, saveAddress]);

    const handleFile = useCallback(async (file: File | null): Promise<string | null> => {
        if (!file) return null;
        if (!UPLOAD_LIMITS.extensions.test(file.name)) {
            return 'Поддерживаются только файлы .xlsx и .xls';
        }
        if (file.size > UPLOAD_LIMITS.maxSize) {
            return 'Файл больше 10 МБ';
        }

        setLastFileName(file.name);
        setIsParsing(true);

        try {
            const { drafts, skippedRows, unknownHeaders } = await parsePositionsFile(file);

            if (!drafts.length) {
                return 'В файле не найдено ни одной позиции с наименованием — проверьте, что файл по шаблону';
            }

            setPendingPositions((prev) => [
                ...prev,
                ...drafts.map((draft, index) => draftToLocalPosition(draft, prev.length + index)),
            ]);

            setJustUploaded(true);
            if (successTimer.current) clearTimeout(successTimer.current);
            successTimer.current = setTimeout(() => setJustUploaded(false), SUCCESS_SCREEN_MS);

            const notes: string[] = [];
            if (skippedRows) notes.push(`Пропущено строк без наименования: ${skippedRows}`);
            if (unknownHeaders.length) {
                notes.push(`Колонки не распознаны и пропущены: ${unknownHeaders.join(', ')}`);
            }

            return notes.length ? notes.join('. ') : null;
        } catch (error) {
            return error instanceof Error && error.message
                ? error.message
                : 'Не удалось разобрать файл заявки';
        } finally {
            setIsParsing(false);
        }
    }, []);

    const runSearch = useCallback(
        async (projectRegion = ''): Promise<{ started: number; duplicates: number } | string> => {
            if (!projectId || !reportId) {
                return 'Отчёт проекта ещё не загружен, попробуйте через секунду';
            }
            if (!pendingPositions.length) return 'Добавьте позиции — запускать поиск не по чему';

            try {
                const response = await startProductsSearch({
                    projectId,
                    reportId,
                    products: pendingPositions.map((position) =>
                        positionToSearchProduct({
                            ...position,
                            region: position.region || projectRegion,
                        }),
                    ),
                }).unwrap();

                const duplicates = response.products.filter((product) => product.is_duplicate).length;

                setPendingPositions([]);
                setAwaitingSearch(true);
                setTotalFloor((productsPage?.total ?? 0) + response.products.length - duplicates);
                floorArmed.current = true;
                setRestoredCount(0);
                setForcePolling(true);

                return { started: response.products.length - duplicates, duplicates };
            } catch {
                return '';
            }
        },
        [projectId, reportId, pendingPositions, startProductsSearch, productsPage]
    );

    const logisticsCount = useMemo(
        () => positions.filter((position) => position.logistics).length,
        [positions],
    );

    return {
        positions,
        uiState,
        page,
        pageCount,
        total: Math.max(total, totalFloor),
        deliveryAvgTotal,
        isPageLoading,
        setPage,
        lastFileName,
        deliveryAddress,
        logisticsCount,
        reportId,
        isDeliveryProcessing:
            delivery?.isProcessing ??
            report?.delivery_processing_status === REPORT_STATUS.PROCESSING,
        delivery,
        progress,
        isLoading: isReportPending || isProductsPending,
        isSearching,
        awaitingSearch,
        isReportFinished: Boolean(reportId) && !isReportBusy && !isReportLoading,
        pendingCount: pendingPositions.length,
        isStartingSearch,
        setSearchRunning: setForcePolling,
        addPositions,
        removePosition,
        updatePosition,
        toggleFlag,
        setFlagAll,
        dirtyIds,
        bulkDirtyIds,
        restartPosition,
        discardEdits,
        clearAll,
        clearDrafts,
        setDeliveryAddress: setAddressDraft,
        commitDeliveryAddress,
        handleFile,
        runSearch,
        branchesLeft: report?.branches_processing ?? 0,
        reportStatus,
        screenshots: report?.screenshots,
        isScreenshotting,
        restoredCount,
    };
}